<template>
  <div>
    <navbar :links="links" />
    <!-- No access yet warning -->
    <v-slide-y-transition>
      <v-layout
        v-if="!loading && viewedUser && viewedUserHasNoAccessYet"
        class="mt-3 px-3 py-2 warning white--text subheading"
        align-center
      >
        <v-icon
          class="mr-3"
          x-large
          color="white"
        >
          warning
        </v-icon>
        <div v-if="isAllowedToManageThisUser">
          {{ $t('userManagementUserHasNoAccessYetHintForAdmin') }}
        </div>
        <div v-else-if="$user == viewedUser">
          {{ $t('userManagementUserHasNoAccessYetHintForUser') }}
        </div>
      </v-layout>
    </v-slide-y-transition>
    <div
      v-if="!(viewedUser && $user) || loading"
      class="loading-indicator"
    >
      <v-progress-circular
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <v-layout
      v-else
      column
      class="mt-2"
    >
      <v-layout
        align-center
        wrap
        class="mt-3"
      >
        <!-- Avatar -->
        <user-avatar-image
          v-if="viewedUser && viewedUser.clientprofile.avatar_image_url"
          :user="viewedUser"
          :size="60"
          class="mr-3"
        />
        <!-- Rename User -->
        <v-layout column>
          <edit-managed-user-names
            :user="viewedUser"
            :show-edit-button="isAllowedToRenameThisUser"
            class="title"
            :small="false"
          />
          <div class="grey--text text--darken-1 mt-1">
            {{ viewedUser.email }}
          </div>
        </v-layout>
        <!-- Twofactor Authentication -->
        <div
          v-if="viewedUser.clientprofile.twofactorauth_enabled"
          class="mt-3"
        >
          <v-layout
            column
            align-center
          >
            <img
              v-if="viewedUser.clientprofile.twofactorauth_enabled == 'sms'"
              :src="IMAGE_TWO_FACTOR_AUTH_VIA_SMS"
              height="48px"
              :title="$t('userManagementUserTwoFactorAuthViaSMS')"
            >
            <img
              v-else-if="
                viewedUser.clientprofile.twofactorauth_enabled == 'otp'
              "
              :src="IMAGE_TWO_FACTOR_AUTH_VIA_APP"
              height="48px"
              :title="$t('userManagementUserTwoFactorAuthViaApp')"
            >
            <v-btn
              v-if="isAllowedToManageThisUser"
              small
              flat
              round
              @click="confirmTwofactorAuthRemoval()"
            >
              {{ $t('userManagementUserTwoFactorAuthDeleteButtonLabel') }}
            </v-btn>
          </v-layout>
        </div>
      </v-layout>
      <v-layout
        align-center
        wrap
      >
        <!-- Toggle Coadmin -->
        <v-switch
          v-if="isAllowedToManageThisUser && $store.getters.isUsermanager"
          :key="switchRenderKey_"
          :input-value="isManagedCoadmin"
          :loading="updatingManagedCoadmin"
          :label="$t('userManagementCoadminSwitchLabel')"
          :hint="$t('userManagementCoadminSwitchHint')"
          persistent-hint
          color="msr"
          class="mr-2"
          style="flex-grow: 0"
          @change="confirmChangingIsManagedCoadmin"
        />
        <!-- Delete User -->
        <v-hover v-if="isAllowedToManageThisUser">
          <v-btn
            slot-scope="{hover}"
            class="elevation-0 mr-0"
            :class="{
              'ml-auto': $vuetify.breakpoint.smAndUp,
              'mt-4': $vuetify.breakpoint.xs,
            }"
            :block="$vuetify.breakpoint.xs"
            color="error"
            round
            data-cy="delete-user-btn"
            :flat="!hover"
            :title="$t('deleteUser') + ': ' + viewedUser.email"
            @click.stop="deleteManagedUser(viewedUser)"
          >
            <v-icon left>
              delete
            </v-icon>
            {{ $t('deleteUser') }}
          </v-btn>
        </v-hover>
      </v-layout>
      <!-- Coadmins inherit access and cannot be managed in detail -->
      <user-management-coadmin-hint
        v-if="showCoadminHint"
        :user="viewedUser"
      />
      <!-- Existing GroupAccess -->
      <v-layout
        v-if="showAccesses && $user.groupaccesses.length > 0"
        column
        class="mt-3"
      >
        <v-subheader class="pl-0">
          {{ $t('groupaccess') }}
          <span
            v-if="viewedUser.groupaccesses.length"
          >&nbsp;({{ viewedUser.groupaccesses.length }})</span>
        </v-subheader>
        <v-card
          flat
          class="mt-2"
        >
          <v-card-title class="py-0 grey--text text--darken-2">
            <div class="mb-2">
              {{ $t('userManagementGroupAccessHint_1') }}&nbsp;
              <span
                v-if="$user.id !== viewedUser.id"
              >{{ $t('userManagementGroupAccessHint_2a') }}&nbsp;</span>
              <span
                v-else
              >{{ $t('userManagementGroupAccessHint_2b') }}&nbsp;</span>
              <span
                v-if="
                  $store.getters.userIsBeingManaged &&
                    $user.id === viewedUser.id
                "
              >{{ $t('userManagementGroupAccessHint_3') }}&nbsp;</span>
            </div>
          </v-card-title>
        </v-card>
        <group-access-permissions-table
          :user="viewedUser"
          :available-groupaccesses="$user.groupaccesses"
          :actual-groupaccesses="viewedUser.groupaccesses"
        />
      </v-layout>
      <!-- Existing StoreAccess -->
      <v-layout
        v-if="showAccesses && $user.storeaccesses.length > 0"
        column
        class="mt-3"
      >
        <v-subheader class="pl-0">
          {{ $t('explicitStoreaccess') }}
          <span
            v-if="viewedUser.storeaccesses.length"
          >&nbsp;({{ viewedUser.storeaccesses.length }})</span>
        </v-subheader>
        <v-card
          flat
          class="mt-2"
        >
          <v-card-title class="py-0 grey--text text--darken-2">
            <div column>
              <span v-if="$user.id === viewedUser.id">
                {{ $t('userManagementStoreAccessHint_1b') }}
                {{ $t('userManagementGroupAccessHint_2b') }}
                {{ $t('userManagementGroupAccessHint_2c') }}
              </span>
              <span v-else>
                <span v-if="!$store.getters.userIsBeingManaged">{{
                  $t('userManagementStoreAccessHint_1a')
                }}</span>
                <span v-else>
                  {{ $t('userManagementStoreAccessHint_1b') }}
                  {{ $t('userManagementStoreAccessHint_2') }}
                </span>
              </span>
              <!-- Allow adding new StoreAccesses based on what $user can see -->
              <v-layout v-if="$user.id !== viewedUser.id">
                <v-slide-y-transition mode="out-in">
                  <create-store-access-widget
                    v-if="addableStoreAccessItems.length > 0"
                    class="mt-2"
                    style="max-width: 490px"
                    :items="addableStoreAccessItems"
                    :loading="creatingStoreAccess"
                    @selected="onStoreAccessSelected"
                  />
                  <div
                    v-else
                    class="mt-3 info--text"
                  >
                    {{ $t('userManagementStoreAccessAddInfo') }}
                  </div>
                </v-slide-y-transition>
              </v-layout>
            </div>
          </v-card-title>
        </v-card>
        <!-- List and configure existing StoreAccesses of managed User -->
        <store-access-permissions-table
          v-if="viewedUser.storeaccesses.length > 0"
          class="mb-2"
          :user="viewedUser"
          :available-storeaccesses="$user.storeaccesses"
          :actual-storeaccesses="viewedUser.storeaccesses"
        />
      </v-layout>
      <div style="min-height: 60px" />
    </v-layout>
  </div>
</template>

<script>
import {
  IMAGE_TWO_FACTOR_AUTH_VIA_APP,
  IMAGE_TWO_FACTOR_AUTH_VIA_SMS,
} from '@/store';

import accessConstants from '@/mixins/accessConstants.js';
import commonUsermanagement from '@/mixins/commonUsermanagement.js';

import CreateStoreAccessWidget from '@/components/UserManagement/CreateStoreAccessWidget';
import EditManagedUserNames from '@/components/UserManagement/EditManagedUserNames';
import GroupAccessPermissionsTable from '@/components/UserManagement/GroupAccessPermissionsTable';
import Navbar from '@/components/Navbar';
import StoreAccessPermissionsTable from '@/components/UserManagement/StoreAccessPermissionsTable';
import UserAvatarImage from '@/components/UserAvatarImage';
import UserManagementCoadminHint from '@/components/UserManagement/UserManagementCoadminHint';

export default {
  components: {
    CreateStoreAccessWidget,
    EditManagedUserNames,
    GroupAccessPermissionsTable,
    Navbar,
    StoreAccessPermissionsTable,
    UserAvatarImage,
    UserManagementCoadminHint,
  },
  mixins: [accessConstants, commonUsermanagement],
  data() {
    return {
      IMAGE_TWO_FACTOR_AUTH_VIA_APP: IMAGE_TWO_FACTOR_AUTH_VIA_APP,
      IMAGE_TWO_FACTOR_AUTH_VIA_SMS: IMAGE_TWO_FACTOR_AUTH_VIA_SMS,

      loading: false,
      switchRenderKey_: 0,
      updatingManagedCoadmin: false,
      creatingStoreAccess: false,
      selectedStoreAccessToAdd: null,
    };
  },
  computed: {
    links() {
      const links = [
        'userManagementManage',
        {label: this.routeName, active: false},
      ];
      if (this.$vuetify.breakpoint.xs) {
        return links;
      }
      return ['dashboard'].concat(links);
    },
    /**	Get User by id from route. */
    viewedUser() {
      const userIdFromRoute = this.$route.params.userId;
      if (this.$user && userIdFromRoute == this.$user.id) {
        return this.$user;
      }
      for (let i = 0; i < this.managedUsers.length; i++) {
        const user = this.managedUsers[i];
        if (userIdFromRoute == user.id) {
          return user;
        }
      }
      return null;
    },
    viewedUserHasNoAccessYet() {
      return (
        !!this.viewedUser &&
        this.viewedUser.groupaccesses.length === 0 &&
        this.viewedUser.storeaccesses.length === 0
      );
    },
    /**
     * Toggle the is_managed_coadmin flag on the viewed User.
     */
    isManagedCoadmin: {
      get() {
        return this.viewedUser.clientprofile.is_managed_coadmin;
      },
      set(isManagedCoadmin) {
        this.updatingManagedCoadmin = true;
        this.$api
          .updateIsManagedCoadmin(this.viewedUser.id, isManagedCoadmin)
          .then(() => {
            const successTemplate = isManagedCoadmin
              ? this.$t('userManagementCoadminToggledOn')
              : this.$t('userManagementCoadminToggledOff');
            const successMessage = successTemplate.replace(
              '{{ user }}',
              this.viewedUser.first_name + ' ' + this.viewedUser.last_name
            );
            this.$bus.$emit('alert-success', {
              message: successMessage,
              translate: false,
            });

            // Ensure accesses are updated with e.g. implicit ones
            this.fetchUserDataAsNeeded({force: true});
          })
          .catch((error) => {
            console.error(error);

            this.$bus.$emit('alert-error', {
              message: 'genericFailFeedback',
              translate: true,
            });
          })
          .finally(() => {
            this.updatingManagedCoadmin = false;
          });
      },
    },
    bothCoAdmins() {
      if (!this.viewedUser || !this.$user) {
        return false;
      }
      return (
        this.viewedUser.clientprofile.is_managed_coadmin &&
        this.$user.clientprofile.is_managed_coadmin
      );
    },
    isAllowedToRenameThisUser() {
      if (!this.viewedUser || !this.$user) {
        return false;
      }
      const himself = this.viewedUser.id === this.$user.id;
      return (
        himself ||
        (this.$store.getters.isUsermanagerOrManagedCoadmin &&
          !this.bothCoAdmins)
      );
    },
    isAllowedToManageThisUser() {
      if (!this.viewedUser || !this.$user) {
        return false;
      }
      const notHimself = this.viewedUser.id !== this.$user.id;
      return (
        notHimself &&
        !this.bothCoAdmins &&
        this.$store.getters.isUsermanagerOrManagedCoadmin
      );
    },
    /**
     * Listing accesses for management makes no sense if that User is a coadmin.
     */
    showAccesses() {
      // List accesses for all users except Coadmins when a usermanager
      // is looking at it.
      if (this.$store.getters.isUsermanager) {
        return !this.isManagedCoadmin;
      }
      // List accesses when a Coadmin is looking at it, unless it's also
      // a Coadmin. Can view himself though.
      if (this.$store.getters.isManagedCoadmin) {
        if (this.$user.id === this.viewedUser.id) {
          return true;
        }
        return !this.isManagedCoadmin;
      }
      return !this.bothCoAdmins;
    },
    showCoadminHint() {
      const rolesValid =
        (this.$store.getters.isUsermanager && this.isManagedCoadmin) ||
        this.bothCoAdmins;
      const notHimself = !(
        this.$user &&
        this.viewedUser &&
        this.$user.id === this.viewedUser.id
      );
      return rolesValid && notHimself;
    },
    routeName() {
      return this.viewedUser
        ? `${this.viewedUser.first_name} ${this.viewedUser.last_name}`
        : this.$route.name;
    },
    groupaccessesWithStoreRoleAndFullLevel() {
      return this.viewedUser.groupaccesses.filter((groupaccess) => {
        return (
          groupaccess.role == this.GA_ACCESS_ROLE_STORE &&
          groupaccess.level == this.GA_ACCESS_LEVEL_FULL
        );
      });
    },
    storeIdsAlreadyAccessibleThroughGroupAccess() {
      let combined = [];
      this.groupaccessesWithStoreRoleAndFullLevel.forEach((groupaccess) => {
        combined = combined.concat(groupaccess.accessible_store_ids);
      });
      return combined;
    },
    addableStoreAccessItems() {
      const storeIdsAlreadyAccessible = {};
      const addable = [];

      // Assemble list of store IDs that we can already access.
      for (let storeId of this.storeIdsAlreadyAccessibleThroughGroupAccess) {
        storeIdsAlreadyAccessible[storeId] = true;
      }
      for (let storeaccess of this.viewedUser.storeaccesses) {
        storeIdsAlreadyAccessible[storeaccess.store.id] = true;
      }

      // Exclude stores we can already access from returned list.
      for (let storeaccess of this.$user.storeaccesses) {
        if (storeIdsAlreadyAccessible[storeaccess.store.id]) {
          continue;
        }
        addable.push(storeaccess);
        storeIdsAlreadyAccessible[storeaccess.store.id] = true;
      }
      return addable;
    },
  },
  created() {
    this.fetchUserDataAsNeeded();
  },
  methods: {
    confirmTwofactorAuthRemoval() {
      const vm = this;
      const userLabel =
        (this.viewedUser.first_name + ' ' + this.viewedUser.last_name).trim() ||
        this.viewedUser.email;
      const confirmText = this.$t(
        'userManagementUserTwoFactorAuthDeleteDialogBody',
        {
          user: userLabel,
        }
      );
      this.$bus.$emit('show-dynamic-dialog', {
        title: this.$t('userManagementUserTwoFactorAuthDeleteDialogTitle'),
        titleIcon: 'warning',
        titleIconClass: 'warning--text',
        persistent: true,
        content: `
					<div class="subheading">
						${confirmText}
					</div>
					<div class="subheading mt-3">
						${this.$t('userManagementUserTwoFactorAuthDeleteDialogFooter')}
					</div>
				`,
        onConfirm() {
          const dialog = this;
          dialog.loading = true;

          vm.$api
            .removeTwofactorAuthForManagedUser(vm.viewedUser.id)
            .then(() => {
              vm.fetchUserDataAsNeeded({force: true});

              dialog.loading = false;
              vm.$bus.$emit('hide-dynamic-dialog');
            });
        },
      });
    },
    confirmChangingIsManagedCoadmin(newValue) {
      const vm = this;
      const title = newValue
        ? this.$t('userManagementCoadminToggleOnTitle')
        : this.$t('userManagementCoadminToggleOffTitle');
      const hint = newValue
        ? this.$t('userManagementCoadminToggleOnHint')
        : this.$t('userManagementCoadminToggleOffHint');
      this.$bus.$emit('show-dynamic-dialog', {
        title: title,
        titleIcon: 'supervised_user_circle',
        content: hint,
        persistent: true,
        maxDialogWidth: '480px',
        onAbort() {
          // HACK: Force rerender of v-switch, otherwise it will show the
          //   wrong value. For some reason it won't update via its input-value.
          vm.switchRenderKey_ += 1;
        },
        onConfirm() {
          vm.isManagedCoadmin = newValue;
          this.hideDialog_();
        },
      });
    },
    /**
     * Create a new StoreAccess for the managed User.
     *
     * This will automatically be validated against the $user's scope.
     */
    createManagedUserStoreAccess(user, storeId, level) {
      this.creatingStoreAccess = true;
      return this.$api
        .createManagedUserStoreAccess(user.id, storeId, level)
        .then((response) => {
          const storeaccess = response.data;
          this.$store.commit('ADD_STOREACCESS_TO_MANAGED_USER', {
            user: user,
            storeaccess: storeaccess,
          });
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.creatingStoreAccess = false;
        });
    },
    onStoreAccessSelected(storeaccess) {
      this.createManagedUserStoreAccess(
        this.viewedUser,
        storeaccess.store.id,
        storeaccess.level
      );
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },
    /**
     * Show a confirmation dialog and delete User when confirmed.
     */
    deleteManagedUser(user) {
      const vm = this;

      const question = this.$t('askDeleteUser');
      const warning = this.$t('askDeleteUserWarning');

      this.$bus.$emit('show-dynamic-dialog', {
        title: user.email,
        titleIcon: 'delete',
        content: `
					<div class="subheading">
						<p class="error--text pa-2">
							${warning}
						</p>
						${question}
					</div>
				`,
        onConfirm() {
          const dialog = this;
          dialog.loading = true;

          const success = this.$t('deleteUserSuccess').replace(
            '{{ user }}',
            user.email
          );
          vm.$api
            .deleteManagedUser(user.id)
            .then((response) => {
              dialog.loading = false;

              vm.$bus.$emit('alert-success', {
                message: success,
                translate: false,
              });

              // Remove item from users list.
              this.$store.commit('REMOVE_MANAGED_USER', user);

              vm.$bus.$emit('hide-dynamic-dialog');
              vm.$router.push({name: 'userManagementManage'});
            })
            .catch((error) => {
              console.error(error);

              this.$bus.$emit('alert-error', {
                message: 'genericFailFeedback',
                translate: true,
              });
            })
            .finally(() => {
              dialog.loading = false;
            });
        },
      });
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.scrollToTop();
    });
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/UserManagementUser.vue